import { Folder, Nullable } from '@Models';
import { TYPES } from '@Types';
import { RequestAction } from '@Repository/RequestAction';
import { FolderRepository, RequestFolderSuccess } from '@Repository/builder';
import { RequestContestSuccess } from '@Repository/tournament';
import { inject, injectable } from 'inversify';
import { Result } from '@Redux/type';
import { ArmyListsAction, FoldersAction } from '@Redux/actions';
import { DeleteRepository } from 'app/javascript/bundles/adapters/repository/DeleteRepository';
import { IEditableEntityAction } from '@Redux/IEditableEntityAction';
import folderSlice from '@Redux/reducers/builder/folder.slice';
import { PayloadAction } from '@reduxjs/toolkit';
import { FolderParams } from '@Builder/myFolder/edit/validate';

@injectable()
export class MyFolderAction extends IEditableEntityAction<
  string,
  Folder,
  RequestFolderSuccess
> {
  @inject(TYPES.Repository.FolderRepository)
  private _repository!: FolderRepository;
  @inject(TYPES.Actions.FoldersAction) private _foldersAction!: FoldersAction;
  @inject(TYPES.Actions.ArmyListsAction)
  private _armyListsAction!: ArmyListsAction;

  getDeleteRepository(): DeleteRepository<string> {
    return this._repository;
  }

  public toEvent =
    (id: string): Result<void> =>
    async (dispatch) => {
      dispatch(this.areLoading(true));

      const response = await this._repository.toEvent({ id: id });

      if (this.handleFailure(response, dispatch)) return;

      const success = response as RequestContestSuccess;
      window.location.href = `/tournament/contests/${success.payload.id}`;
    };

  public edit =
    (values: FolderParams): Result<void> =>
    async (dispatch, getState) => {
      dispatch(this.areLoading(true));

      const state = getState();
      const folder = state.folder.entity;
      if (!folder) {
        return;
      }

      const response = await this._repository.update({
        folder: {
          ...folder,
          name: values.name,
          parentId: values.parentId != '' ? values.parentId : null,
          isShare: values.isShare,
        },
        locale: null,
      });

      if (this.handleFailure(response, dispatch)) return;

      const success = response as RequestFolderSuccess;
      dispatch(this.fetchDataSuccess(success.payload));
      dispatch(this.areLoading(false));
      dispatch(folderSlice.actions.cancel());
    };

  public addArmyList =
    (armyListId: string): Result<void> =>
    async (dispatch, getState) => {
      dispatch(this.areLoading(true));

      const state = getState();
      const folderId = state.folder.id!!;

      const response = await this._repository.addArmyList({
        id: folderId,
        armyListId: armyListId,
      });

      if (this.handleFailure(response, dispatch)) return;

      const armyLists =
        state.armyLists.entity?.map((item) => {
          if (item.id !== armyListId) {
            return item;
          }
          return {
            ...item,
            folderId: folderId,
          };
        }) ?? [];
      dispatch(this._armyListsAction.fetchDataSuccess(armyLists));
      dispatch(this.areLoading(false));
      dispatch(folderSlice.actions.cancel());
    };

  protected fetchDataSuccess(item: Folder): PayloadAction<Folder> {
    return folderSlice.actions.setFolder(item);
  }

  protected cacheDataSuccess(item: Folder): PayloadAction<Folder> {
    return folderSlice.actions.setFolder(item);
  }

  protected haveError(message: string): PayloadAction<string> {
    return folderSlice.actions.setError(message);
  }

  protected areLoading(bool: boolean): PayloadAction<boolean> {
    return folderSlice.actions.setLoading(bool);
  }

  protected getCachedItem({
    entityId,
    locale,
  }: {
    entityId: string;
    locale: Nullable<string>;
  }): Promise<Nullable<Folder>> {
    return this._repository.getCachedEntity({
      dataId: entityId,
      locale: locale,
    });
  }

  protected async getItem({
    entityId,
    locale,
  }: {
    entityId: string;
    locale: Nullable<string>;
  }): Promise<RequestAction> {
    return this._repository.getFolder({ folderId: entityId, locale: locale });
  }

  protected subFetchDataBefore =
    ({ entity }: { entity: Folder }): Result<void> =>
    (dispatch) => {
      dispatch(this._foldersAction.fetchData());
      dispatch(this._armyListsAction.fetchData());
    };

  protected subFetchDataAfter =
    ({
      entity,
      cachedItem,
    }: {
      entity: Folder;
      cachedItem: Nullable<Folder>;
    }): Result<void> =>
    (dispatch) => {
      dispatch(this._foldersAction.fetchData());
      dispatch(this._armyListsAction.fetchData());
    };
}
